import { sendGAEvent } from "@next/third-parties/google";

interface QueuedEvent {
  eventName: string;
  parameters: any;
  timestamp: number;
}

class AnalyticsBatcher {
  private queue: QueuedEvent[] = [];
  private batchSize = 5;
  private flushInterval = 2000; // 2 seconds
  private timer: NodeJS.Timeout | null = null;
  private isProcessing = false;

  constructor() {
    // Start the flush timer
    this.startTimer();

    // Flush on page unload
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", () => {
        this.flush();
      });

      // Also flush on visibility change (when user switches tabs)
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
          this.flush();
        }
      });
    }
  }

  addEvent(eventName: string, parameters: any) {
    const queuedEvent: QueuedEvent = {
      eventName,
      parameters,
      timestamp: Date.now(),
    };

    this.queue.push(queuedEvent);

    // Development logging
    if (process.env.NODE_ENV === "development") {
      console.log(`📥 GA Event Queued:`, {
        event: eventName,
        parameters,
        queueLength: this.queue.length,
        willFlushNow: this.queue.length >= this.batchSize,
        timestamp: new Date().toISOString(),
      });
    }

    // Flush immediately if we've reached the batch size
    if (this.queue.length >= this.batchSize) {
      this.flush();
    }
  }

  private startTimer() {
    if (this.timer) {
      clearInterval(this.timer);
    }

    this.timer = setInterval(() => {
      if (this.queue.length > 0) {
        this.flush();
      }
    }, this.flushInterval);
  }

  private async flush() {
    if (this.isProcessing || this.queue.length === 0) {
      return;
    }

    this.isProcessing = true;
    const eventsToSend = [...this.queue];
    this.queue = [];

    // Development logging
    if (process.env.NODE_ENV === "development") {
      console.log(`🚀 Flushing ${eventsToSend.length} GA Events:`, {
        events: eventsToSend.map((e) => ({
          name: e.eventName,
          timestamp: new Date(e.timestamp).toISOString(),
        })),
        batchSize: eventsToSend.length,
        timestamp: new Date().toISOString(),
      });
    }

    try {
      // Send events in parallel for better performance
      const promises = eventsToSend.map((event) =>
        sendGAEvent("event", event.eventName, event.parameters)
      );

      await Promise.all(promises);

      // Development success logging
      if (process.env.NODE_ENV === "development") {
        console.log(`✅ Successfully sent ${eventsToSend.length} GA events`);
      }
    } catch (error) {
      console.warn("Failed to send analytics events:", error);

      // Development error logging
      if (process.env.NODE_ENV === "development") {
        console.error(
          `❌ Failed to send ${eventsToSend.length} GA events:`,
          error
        );
      }

      // Re-queue failed events (but limit to prevent infinite growth)
      if (this.queue.length < 50) {
        this.queue.unshift(...eventsToSend.slice(0, 10)); // Only re-queue first 10
      }
    } finally {
      this.isProcessing = false;
    }
  }

  // Method to manually flush (useful for testing)
  forceFlush() {
    return this.flush();
  }

  // Get queue status for debugging
  getQueueStatus() {
    return {
      queueLength: this.queue.length,
      isProcessing: this.isProcessing,
      oldestEvent: this.queue[0]?.timestamp || null,
    };
  }
}

// Create singleton instance
const analyticsBatcher = new AnalyticsBatcher();

// Export function to add events to the batch
export const batchedSendGAEvent = (eventName: string, parameters: any) => {
  analyticsBatcher.addEvent(eventName, parameters);
};

// Export for debugging
export const getAnalyticsBatcherStatus = () =>
  analyticsBatcher.getQueueStatus();
export const flushAnalyticsBatcher = () => analyticsBatcher.forceFlush();
